"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiMapPin, PiPhoneCall, PiEnvelopeSimple, PiFacebookLogo, PiTwitterLogo, PiInstagramLogo, PiYoutubeLogo, PiArrowRight } from "react-icons/pi";
import { socket } from "@/app/support/lib/socket";
import homepageApi from "@/services/homepageApi";
import logger from "@/utils/logger";

export default function EducaeetCTA({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryTheme = content.colorPalette?.primary || "#074d3d"; // Deep forest emerald
    const fontFamily = content.fontFamily || "'Inter', 'Plus Jakarta Sans', sans-serif";

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
    });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Get institution ID from local storage or context (stored during login or site load)
        const institutionId = typeof window !== "undefined"
            ? (localStorage.getItem("subdomain_institution_uuid") || localStorage.getItem("subdomain_institution_id") || localStorage.getItem("userId") || "institution_default")
            : "institution_default";

        // Save Lead Query directly to SQL support_tickets database and broadcast via socket
        try {
            await homepageApi.submitQuery({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message
            });
        } catch (error) {
            logger.error("Failed to submit lead query to API:", error);
        }

        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 4000);
    };

    return (
        <div
            className={`relative py-20 md:py-28 overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ backgroundColor: "#ffffff", fontFamily }}
        >
            <div className="container mx-auto px-6 max-w-7xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    
                    {/* LEFT COLUMN - CONTACT INFO & SOCIALS */}
                    <div className="lg:col-span-5 text-left">
                        <h2 className="text-3xl sm:text-4xl lg:text-[40px] font-bold text-gray-900 leading-tight mb-4 tracking-tight">
                            <EditableText
                                value={content.title || "I will Answer all Your Questions"}
                                onChange={(v) => onContentChange?.({ ...content, title: v })}
                            />
                        </h2>

                        <p className="text-gray-500 text-sm sm:text-base leading-relaxed mb-8">
                            <EditableText
                                value={content.description || "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy.."}
                                onChange={(v) => onContentChange?.({ ...content, description: v })}
                            />
                        </p>

                        {/* Contact Info Header */}
                        <h3 className="text-xl font-bold text-gray-900 mb-6 tracking-tight">Contact Info</h3>


                        <div className="space-y-5 mb-10">
                            {/* Location Item */}
                            <div className="flex items-center gap-4">
                                <div className="w-13 h-13 rounded-2xl border-2 border-[#074d3d] bg-emerald-50/60 flex items-center justify-center shrink-0 text-[#074d3d] shadow-xs">
                                    <PiMapPin className="w-6 h-6" />
                                </div>
                                <div className="text-sm font-semibold text-gray-700 leading-snug">
                                    <EditableText
                                        value={content.address || "4419 Simpson Avenue Belleville, PA 17004 Ney York"}
                                        onChange={(v) => onContentChange?.({ ...content, address: v })}
                                    />
                                </div>
                            </div>

                            {/* Phone Item */}
                            <div className="flex items-center gap-4">
                                <div className="w-13 h-13 rounded-2xl border-2 border-[#074d3d] bg-emerald-50/60 flex items-center justify-center shrink-0 text-[#074d3d] shadow-xs">
                                    <PiPhoneCall className="w-6 h-6" />
                                </div>
                                <div className="text-sm font-semibold text-gray-700">
                                    <EditableText
                                        value={content.phone || "+1 (305) 902-3242"}
                                        onChange={(v) => onContentChange?.({ ...content, phone: v })}
                                    />
                                </div>
                            </div>

                            {/* Email Item */}
                            <div className="flex items-center gap-4">
                                <div className="w-13 h-13 rounded-2xl border-2 border-[#074d3d] bg-emerald-50/60 flex items-center justify-center shrink-0 text-[#074d3d] shadow-xs">
                                    <PiEnvelopeSimple className="w-6 h-6" />
                                </div>
                                <div className="text-sm font-semibold text-gray-700">
                                    <EditableText
                                        value={content.email || "Instacks@gamil.com"}
                                        onChange={(v) => onContentChange?.({ ...content, email: v })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Social Media Header & Icons */}
                        <h3 className="text-lg font-bold text-gray-900 mb-4 tracking-tight">Social Media</h3>
                        <div className="flex items-center gap-4 text-gray-600 text-xl">
                            <a href={content.facebookUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-[#074d3d] transition-colors"><PiFacebookLogo className="w-6 h-6" /></a>
                            <a href={content.twitterUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-[#074d3d] transition-colors"><PiTwitterLogo className="w-6 h-6" /></a>
                            <a href={content.instagramUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-[#074d3d] transition-colors"><PiInstagramLogo className="w-6 h-6" /></a>
                            <a href={content.youtubeUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-[#074d3d] transition-colors"><PiYoutubeLogo className="w-6 h-6" /></a>
                        </div>
                    </div>

                    {/* RIGHT COLUMN - CONTACT FORM CARD WITH OFFSET FRAME */}
                    <div className="lg:col-span-7 relative">
                        {/* Offset Dark Green Frame Backdrop matching screenshot */}
                        <div className="absolute inset-0 translate-x-2.5 translate-y-2.5 rounded-[32px] bg-[#074d3d] -z-10" />

                        <div className="bg-white rounded-[32px] p-8 sm:p-10 md:p-12 shadow-xl border border-gray-100 relative z-10 text-left">
                            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 tracking-tight">
                                <EditableText
                                    value={content.formTitle || "Submit Your Query"}
                                    onChange={(v) => onContentChange?.({ ...content, formTitle: v })}
                                />
                            </h3>


                            {submitted ? (
                                <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-center font-bold text-lg animate-fade-in">
                                    Thank you! Your query has been submitted successfully.
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Row 1: First Name & Email */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                <EditableText
                                                    value={content.formLabelName || "First Name"}
                                                    onChange={(v) => onContentChange?.({ ...content, formLabelName: v })}
                                                />
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={content.formPlaceholderName || "Name"}
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-4 py-3.5 rounded-xl bg-slate-100/70 border border-transparent focus:border-[#074d3d] focus:bg-white focus:outline-none text-gray-800 text-sm font-medium transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                <EditableText
                                                    value={content.formLabelEmail || "Email"}
                                                    onChange={(v) => onContentChange?.({ ...content, formLabelEmail: v })}
                                                />
                                            </label>
                                            <input
                                                type="email"
                                                placeholder={content.formPlaceholderEmail || "Email"}
                                                required
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full px-4 py-3.5 rounded-xl bg-slate-100/70 border border-transparent focus:border-[#074d3d] focus:bg-white focus:outline-none text-gray-800 text-sm font-medium transition-all"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 2: Phone & Subject */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                <EditableText
                                                    value={content.formLabelPhone || "Phone"}
                                                    onChange={(v) => onContentChange?.({ ...content, formLabelPhone: v })}
                                                />
                                            </label>
                                            <input
                                                type="tel"
                                                placeholder={content.formPlaceholderPhone || "Phone"}
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                className="w-full px-4 py-3.5 rounded-xl bg-slate-100/70 border border-transparent focus:border-[#074d3d] focus:bg-white focus:outline-none text-gray-800 text-sm font-medium transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                <EditableText
                                                    value={content.formLabelSubject || "Subject"}
                                                    onChange={(v) => onContentChange?.({ ...content, formLabelSubject: v })}
                                                />
                                            </label>
                                            <select
                                                value={formData.subject}
                                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                                className="w-full px-4 py-3.5 rounded-xl bg-slate-100/70 border border-transparent focus:border-[#074d3d] focus:bg-white focus:outline-none text-gray-500 text-sm font-medium transition-all appearance-none cursor-pointer"
                                                style={{
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%6b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "right 1rem center",
                                                    backgroundSize: "1.2em"
                                                }}
                                            >
                                                <option value="">Subject</option>
                                                <option value="course">Course Inquiry</option>
                                                <option value="admission">Admissions</option>
                                                <option value="support">Technical Support</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Row 3: Message */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                            <EditableText
                                                value={content.formLabelMessage || "Message"}
                                                onChange={(v) => onContentChange?.({ ...content, formLabelMessage: v })}
                                            />
                                        </label>
                                        <textarea
                                            rows={5}
                                            placeholder={content.formPlaceholderMessage || "Message"}
                                            required
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            className="w-full px-4 py-3.5 rounded-xl bg-slate-100/70 border border-transparent focus:border-[#074d3d] focus:bg-white focus:outline-none text-gray-800 text-sm font-medium transition-all resize-none"
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-bold text-white shadow-lg transition-transform hover:scale-105 active:scale-95 cursor-pointer text-sm sm:text-base"
                                            style={{ backgroundColor: "#074d3d" }}
                                            onClick={(e) => {
                                                if (onContentChange) e.preventDefault();
                                            }}
                                        >
                                            <span>
                                                <EditableText
                                                    value={content.formButtonText || "Send Message"}
                                                    onChange={(v) => onContentChange?.({ ...content, formButtonText: v })}
                                                />
                                            </span>
                                            <PiArrowRight className="w-4 h-4 stroke-[2]" />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
